/**
 * 파일명 : WithInfoLogin.jsx
 * 설명 : 로그인 화면
 * 수정내역
 ************************************************
 * Version    수정일자     수정자      수정내용
 ************************************************
 * 1.0      2021-02-19   윤성수      최초작성
 *
 */

/************************************************
 * Import 영역
 ************************************************/
/* React */
import React from 'react';

/* AntD */
import { Input, Button, Typography, Layout, Row, Col} from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';

/* 업무 */
import { persistor } from '../../../redux/store';
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { FrameAction } from '../../../redux/slice/frame/root_main_slice';
import { LoginAction } from '../../..//redux/slice/frame/login_slice';
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import loginLogoWithInfo from 'images/loginLogoWithInfo.jpg';
import rightLogoWithInfo from 'images/rightLogoWithInfo.png';

const WithInfoLogin: React.FC = () => {
    // const [showLoading, setShowLoading] = React.useState(false);
    const defaultMenu = 'MCOM2021006';  // MCOM2021006
    const dispatch = useAppDispatch();
    localStorage.setItem('scrnId', 'Login');
    
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const userId = useAppSelector((state) => state.Login.userId);
    const pswd = useAppSelector((state) => state.Login.pswd);
    const userIdRequired = useAppSelector((state) => state.Login.userIdRequired);
    const pswdRequired = useAppSelector((state) => state.Login.pswdRequired);
    const detail = useAppSelector((state) => state.Login.detail);


    React.useEffect(() => {
        persistor.flush().then(() => {
            persistor.purge();
        });
    }, []);
    

    /************************************************
     * 이벤트 함수 영역
     ************************************************/
    /**
     * 기능 : 로그인버튼 클릭
     * 설명 : 입력정합성 검증 후 로그인 서비스를 호출한다.
     * param :
     * return : None
     */
    const fn_Login = async () => {
        if (detail) {
            return false;
        }

        common.showLoading(true);
        /* 변수선언 및 셋팅 영역 */
        const requestData = {
            // 입력파라미터
            userId : userId,
            pswd   : pswd,
        };

        /* 트렌젝션 호출 및 결과 셋팅 */  
        const result = await commonProxy.callSyncSvc(requestData, { txCd : 'TRUSRR0001', noLoading: true });

        if (!result.success) {
            common.showLoading(false);
            const param = {
                title : '위드정보 확인',
                contents: result.response.data.message,
            };

            common.openInfo(param);
            return;
        }

        const jwtToken = result.userItem.jwtToken;
        localStorage.setItem('jwtToken', jwtToken);
        await fn_SearchTopMenus();
        
        const strAllLnbMenuItems = localStorage.getItem('allLnbMenuItems');
        const arrAllLnbMenuItems = JSON.parse(strAllLnbMenuItems);
        
        dispatch(FrameAction.setAllLnbMenuItems(arrAllLnbMenuItems));
        localStorage.removeItem('allLnbMenuItems');
        
        dispatch(FrameAction.setJwtToken(jwtToken));
        dispatch(FrameAction.setActiveKey(defaultMenu));
        dispatch(FrameAction.setCurrentTopMenuKey('MCOM2021005'));
        common.showLoading(false);
    };

    const fn_SearchTopMenus = async () => {
        /* 변수선언 및 셋팅 영역 */
        let requestData = {};

        /* 트렌젝션 호출 및 결과 셋팅 */
        const result = await commonProxy.callSyncSvc(requestData, {
            txCd : 'TRFRMR0001',
            noLoading: true
        });
        

        dispatch(FrameAction.setTopMenuItems(result.menuItems));
        // localStorage.setItem('topMenuItems', JSON.stringify(result.menuItems));
        await getLeftMenus(result.menuItems);
    }

    const getLeftMenus = async (topMenus:any[]) => {
        for (const element of topMenus) {
            await fn_getLeftMenu(element.menuId);
        }
    }

    const fn_getLeftMenu = async (menuId:string) => {
        /* 변수선언 및 셋팅 영역 */
        let   requestData = {};

        requestData = {
            // 입력파라미터
            upperMenuId : menuId,
        };
        const result = await commonProxy.callSyncSvc(requestData, { txCd : 'TRFRMR0002', noLoading: true });

        const filterMenus = result.menuItems.filter((item:any) => {
            return item.isShow === 'Y';
        });

        
        dispatch(FrameAction.setLnbMenuItems(filterMenus.filter((item:any) => {return item.key !== 'MCOM2021008'})));

        let strAllLnbMenuItems = localStorage.getItem('allLnbMenuItems');
        let tmpAllLnbMenuItems;
        let objAllLnbMenuItems : any[];
        if (strAllLnbMenuItems) {
            tmpAllLnbMenuItems = JSON.parse(strAllLnbMenuItems);
            objAllLnbMenuItems = [...tmpAllLnbMenuItems];
            objAllLnbMenuItems = [...objAllLnbMenuItems, ...filterMenus];
        } else {
            objAllLnbMenuItems = [...filterMenus];
        }

        localStorage.setItem('allLnbMenuItems', JSON.stringify(objAllLnbMenuItems));

        const strLnbtMenuItems = localStorage.getItem('allLnbMenuItems');
        const arrLnbtMenuItems = JSON.parse(strLnbtMenuItems);
        const currLnbMenuItems = arrLnbtMenuItems.filter((lnb:any) =>{
            return lnb.upperMenuId === 'MCOM2021005' && lnb.key !== 'MCOM2021008';
        });

        let boardMenus = {};
        const authMenus = result.menuItems.filter((item:any) => {
            return item.upperMenuId === 'MCOM2021005';
        });

        if (authMenus && authMenus.length > 0) {
            result.menuItems.forEach((item:any) => {
                const key = item.key;
                const menu  = item;
                boardMenus = {...boardMenus, [key] : menu};
            });
            dispatch(FrameAction.setAuthBoardItems(boardMenus));
        }

        dispatch(FrameAction.setLnbMenuItems(currLnbMenuItems));
        // dispatch(FrameAction.setActiveKey(defaultMenu));
        // dispatch(FrameAction.setCurrentTopMenuKey('MCOM2021005'));
    }

    /**
     * 기능 : onChange 이벤트 선언
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const onChange = React.useCallback ((e:any) => {
        if (e.currentTarget.name === 'userId') {
            dispatch(LoginAction.setUserId(e.currentTarget.value));
        }
        if (e.currentTarget.name === 'pswd') {
            dispatch(LoginAction.setPswd(e.currentTarget.value));
        }
    },[userId, pswd]);

    const onClick = React.useCallback((e:any) => {
        if (e.currentTarget.name === 'btnLogin') {
            if (!userId) {
                dispatch(LoginAction.setDetail('아이디는 필수입력 항목 입니다.'));
                dispatch(LoginAction.setUserIdRequired(true));
                dispatch(LoginAction.setPswdRequired(false));
                
            } else if (!pswd) {
                dispatch(LoginAction.setDetail('패스워드는 필수입력 항목 입니다.'));
                dispatch(LoginAction.setUserIdRequired(false));
                dispatch(LoginAction.setPswdRequired(true));
            } else {
                dispatch(LoginAction.setDetail(''));
                fn_Login();
            }
        }

        if (e.currentTarget.name === 'btnPwdInit') {
            if (!userId) {
                const param = {
                    title : '위드정보 확인',
                    contents: '비밀번호 초기화를 진행할 아이디를 입력하세요.',
                };

                common.openInfo(param);
                return;
            }

            const param = {
                title : '위드정보 확인',
                contents: '비밀번호를 초기화 하시겠습니까?',
                accept : () => {
                    /* 변수선언 및 셋팅 영역 */
                    const requestData = {
                        // 입력파라미터
                        userId
                    };
            
                    commonProxy.callAsyncSvc(requestData, {
                        txCd : 'TRUSRS0007',
                        success : (_responseData:any) => {
                            const param = {
                                title : '위드정보 확인',
                                contents: '패스워드 초기화 인증메일을 확인하세요.',
                            };
            
                            common.openAlert(param);
                        },
                    });
                }
            };
            common.openConfirm(param);
        }
    },[userId, pswd]);


    /* Render 변수 선언영역 */
    /* AntD */
    const { Text } = Typography;
    const { Content } = Layout;

    const contentStyle = {
        padding: 50,
        background: 'rgba(0, 0, 0, 0.05)',
        borderRadius: 4,
    };

    return(
        /* 화면 디자인 영역 */
        <Layout className='login-background'>
            <Content style={{borderRadius:'10px', border:'1.5px solid #00B4D8'}}>
                <Row style={{alignItems:'center'}}>
                    <Col id='leftPart' style={{width : '45%'}}>
                        <Row>
                            <img src={loginLogoWithInfo} alt='login_logo' width='80%' height='80%' style={{margin : '30px auto'}}/>
                        </Row>
                        <Row style={{width : '70%', margin : '0 auto'}}>
                            <div style={{ width:'100%'}}>
                                <Input className='login-input' 
                                    prefix={<UserOutlined />} 
                                    value={userId}
                                    name='userId'
                                    onChange={onChange} 
                                    inputMode='email'
                                    size='large' />
                                    {userIdRequired ? (
                                    <div><Text type='danger' style={{height:'100%'}}>{detail}</Text></div>
                                    ) : <div><br /></div>}
                            </div>
                        </Row>
                        <Row style={{width : '70%', margin : '0 auto'}}>
                                <div style={{ width:'100%'}}>
                                <Input.Password className='login-input'
                                    name='pswd'  
                                    prefix={<LockOutlined />} 
                                    value={pswd} 
                                    onChange={onChange}
                                    onPressEnter={fn_Login} 
                                    size='large'/>
                                    {pswdRequired ? (
                                    <div><Text type='danger'>{detail}</Text></div>
                                    ) : <div><br /></div>}
                            </div>
                        </Row>
                        <Row style={{width : '70%', margin : '0 auto'}}>
                            <Button name='btnLogin' className='btnLogin' onClick={onClick}>LOGIN</Button>
                        </Row>
                        <Row style={{width : '70%', margin : '0 auto', alignItems:'center', justifyContent:'center', display:'flex'}}>
                            <Button name='btnPwdInit' className='link-button' size='small' onClick={onClick}>비밀번호 초기화</Button>
                        </Row>
                    </Col>
                    <Col id='rightPart' style={{width : '55%'}}>
                        <img id='rightPart' src={rightLogoWithInfo} alt='login_screen' className='right-img' width='100%' height='100%' style={{paddingRight: '40px'}} />
                    </Col>
                </Row>
            </Content>
        </Layout> 
    );
}

export default WithInfoLogin;
